import React, { useMemo } from 'react';
import {
    applyTableFilters,
    applyTableSort
} from '../byor/utils/tableFilterUtils';
import { getLabelStyle } from './widgetLabelHelpers';

// Helper function to calculate pagination slice indices
const getPaginationSlice = (pagination) => {
  if (!pagination) return null;
  const startIndex = pagination.startIndex !== undefined 
    ? pagination.startIndex 
    : pagination.currentPage * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  return { startIndex, endIndex };
};

export const getDisplayHeader = (config, headerName) => {
  return config?.customColumnHeaders?.[headerName] || headerName;
};

const applyHiddenColumns = (config, columnMap) => {
  const hiddenColumns = config?.hiddenColumns || [];
  if (hiddenColumns.length === 0) return {};
  const overrides = {};
  for (const [colName, showKey] of Object.entries(columnMap)) {
    if (hiddenColumns.includes(colName)) {
      overrides[showKey] = false;
    }
  }
  return overrides;
};

// Helper to check if software is a "Microsoft/Windows component"
function isMicrosoftComponent(name, publisher) {
  const n = (name || '').toLowerCase();
  const p = (publisher || '').toLowerCase();
  
  if (p.includes('microsoft') || n.includes('microsoft')) return true;
  
  const msKeywords = [
    'windows',
    'edge webview2'
  ];
  
  return msKeywords.some(keyword => n.includes(keyword));
}

// Data extractor
export function extractSoftwareInventoryData(data, config = {}) {
  console.log('[SoftwareInventoryWidget] Received data:', typeof data, Array.isArray(data) ? data.length : Object.keys(data || {}));
  let networkDevices = [];
  if (Array.isArray(data)) {
    networkDevices = data;
  } else if (data?.network_devices && Array.isArray(data.network_devices)) {
    networkDevices = data.network_devices;
  } else if (data?.scanData?.network_devices && Array.isArray(data.scanData.network_devices)) {
    networkDevices = data.scanData.network_devices;
  } else if (data?.data?.network_devices && Array.isArray(data.data.network_devices)) {
    networkDevices = data.data.network_devices;
  } else {
    // Try to find a property containing 'network' that is an array
    const keys = Object.keys(data || {});
    const networkKey = keys.find(k => k.toLowerCase().includes('network') && Array.isArray(data[k]));
    if (networkKey) {
      networkDevices = data[networkKey];
    }
  }
  
  const hideMicrosoft = config.hideMicrosoft === true;
  const groupIdentical = config.groupIdentical === true;
  const hostThreshold = parseInt(config.hostThreshold, 10) || 1;
  
  let allSoftware = [];

  // Step 1: Extract all software entries per host
  networkDevices.forEach(device => {
    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const system = device.system || {};
    let rawSoftwares = system.softwares || system.installed_software || system.installed_softwares || [];
    
    let softwares = [];
    if (Array.isArray(rawSoftwares)) {
      softwares = rawSoftwares;
    } else if (typeof rawSoftwares === 'object' && rawSoftwares !== null) {
      // Handle format where installed_softwares is an object: { "Software Name": "Version" } or { "Software Name": { "Version": "...", "Publisher": "..." } }
      softwares = Object.entries(rawSoftwares).map(([name, val]) => {
        if (val && typeof val === 'object') {
          return {
            Name: name,
            Version: val.Version || val.version || 'Unknown',
            Publisher: val.Publisher || val.publisher || 'Unknown'
          };
        } else {
          return {
            Name: name,
            Version: val || 'Unknown',
            Publisher: 'Unknown'
          };
        }
      });
    }

    // Deduplicate identical software on the SAME host to prevent multiple rows per host
    const seenOnHost = new Set();

    softwares.forEach(software => {
      let name = software.Name || software.name || 'Unknown Software';
      let version = software.Version || software.version || '';
      let publisher = software.Publisher || software.publisher || '';

      if (name && typeof name === 'object') {
        name = name.Name || name.name || JSON.stringify(name);
      }
      if (version && typeof version === 'object') {
        version = version.Version || version.version || JSON.stringify(version);
      }
      if (publisher && typeof publisher === 'object') {
        publisher = publisher.Publisher || publisher.publisher || JSON.stringify(publisher);
      }

      // Ensure they are string primitives to prevent React rendering crashes
      name = String(name);
      version = String(version);
      publisher = String(publisher);

      const uniqueKey = `${name}|${version}`;
      
      if (!seenOnHost.has(uniqueKey)) {
        seenOnHost.add(uniqueKey);
        
        let shouldInclude = true;
        if (hideMicrosoft && isMicrosoftComponent(name, publisher)) {
          shouldInclude = false;
        }

        if (shouldInclude) {
          allSoftware.push({
            ip,
            hostname,
            name,
            publisher,
            version,
            // Original object for reference if needed
            ...software,
            Name: name,
            Version: version,
            Publisher: publisher
          });
        }
      }
    });
  });

  // Step 2: Grouping & Host Threshold Logic
  if (groupIdentical || hostThreshold > 1) {
    const grouped = {};
    
    allSoftware.forEach(item => {
      const key = `${item.name}|${item.version}`;
      if (!grouped[key]) {
        grouped[key] = {
          name: item.name,
          publisher: item.publisher,
          version: item.version,
          hostnames: new Set(),
          ips: new Set(),
          items: []
        };
      }
      if (item.hostname) grouped[key].hostnames.add(item.hostname);
      if (item.ip) grouped[key].ips.add(item.ip);
      grouped[key].items.push(item);
    });

    // Filter by threshold
    const filteredGroupKeys = Object.keys(grouped).filter(key => {
      return grouped[key].hostnames.size >= hostThreshold;
    });

    if (groupIdentical) {
      // Return grouped format
      return filteredGroupKeys.map(key => {
        const group = grouped[key];
        return {
          name: group.name,
          publisher: group.publisher,
          version: group.version,
          // Convert Set to sorted array and join for searchability
          hostnames: Array.from(group.hostnames).sort().join(', '),
          // Keep raw count for specialized rendering if needed
          hostCount: group.hostnames.size
        };
      });
    } else {
      // Return per-workstation format but only for software meeting threshold
      let thresholdFilteredSoftware = [];
      filteredGroupKeys.forEach(key => {
        thresholdFilteredSoftware.push(...grouped[key].items);
      });
      return thresholdFilteredSoftware;
    }
  }

  // Default: Per-workstation mode with threshold = 1
  return allSoftware;
}

export const SoftwareInventoryTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const isGrouped = config.groupIdentical === true;

  // Define base columns based on mode
  const baseOverrides = isGrouped 
    ? { 'No.': 'showNo', 'Hostnames': 'showHostnames', 'Software Name': 'showName', 'Publisher': 'showPublisher', 'Version/Build': 'showVersion' }
    : { 'No.': 'showNo', 'IP': 'showIP', 'Hostname': 'showHostname', 'Software Name': 'showName', 'Publisher': 'showPublisher', 'Version/Build': 'showVersion' };

  const hiddenOverrides = applyHiddenColumns(config, baseOverrides);
  
  // Default show props based on mode
  const defaultShow = isGrouped
    ? { showNo: true, showHostnames: true, showName: true, showPublisher: true, showVersion: true }
    : { showNo: true, showIP: true, showHostname: true, showName: true, showPublisher: true, showVersion: true };

  const {
    title = 'Software Inventory',
    ...restConfig
  } = { ...config, ...defaultShow, ...hiddenOverrides };

  const widgetType = 'software-inventory-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Serialize config for deep memoization since restConfig is created freshly every render
  const serializedConfig = JSON.stringify(restConfig);

  // Extract data using memo
  const allSoftwareData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractSoftwareInventoryData(data, restConfig), restConfig.tableFilters),
      restConfig.tableSort
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, serializedConfig]);

  // Apply pagination if provided
  const displayData = useMemo(() => {
    if (!pagination) return allSoftwareData;
    const slice = getPaginationSlice(pagination);
    return allSoftwareData.slice(slice.startIndex, slice.endIndex);
  }, [allSoftwareData, pagination]);

  // Dynamic grid columns
  let templateColumns = '';
  if (isGrouped) {
    const columns = [
      restConfig.showNo ? '0.5fr' : '',
      restConfig.showHostnames ? '2fr' : '',
      restConfig.showName ? '2fr' : '',
      restConfig.showPublisher ? '1.5fr' : '',
      restConfig.showVersion ? '1fr' : ''
    ].filter(Boolean);
    templateColumns = columns.join(' ') || '1fr';
  } else {
    const columns = [
      restConfig.showNo ? '0.5fr' : '',
      restConfig.showIP ? '1fr' : '',
      restConfig.showHostname ? '1.5fr' : '',
      restConfig.showName ? '2fr' : '',
      restConfig.showPublisher ? '1.5fr' : '',
      restConfig.showVersion ? '1fr' : ''
    ].filter(Boolean);
    templateColumns = columns.join(' ') || '1fr';
  }

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left' // Will be overridden by rowStyleProp alignment if set
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title ? <h3 className="findings-widget-title" style={titleStyle}>{title}</h3> : null}

      <div className="open-ports-table">
          {/* Header Row */}
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {restConfig.showNo ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'No.')}</div> : null}
            
            {(!isGrouped && restConfig.showIP) ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'IP')}</div> : null}
            {(!isGrouped && restConfig.showHostname) ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'Hostname')}</div> : null}
            
            {(isGrouped && restConfig.showHostnames) ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'Hostnames')}</div> : null}
            
            {restConfig.showName ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'Software Name')}</div> : null}
            {restConfig.showPublisher ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'Publisher')}</div> : null}
            {restConfig.showVersion ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'Version/Build')}</div> : null}
          </div>

          {/* Data Rows */}
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <span style={{ fontStyle: 'italic', margin: 0 }}>No software inventory records match selected filters.</span>
              </div>
            ) : (
              <React.Fragment>
                {displayData.map((item, idx) => {
                  const startIndex = pagination && pagination.startIndex !== undefined
                    ? pagination.startIndex
                    : (pagination ? pagination.currentPage * pagination.itemsPerPage : 0);
                  const actualNumber = startIndex + idx + 1;
                  
                  // Use a robust key that does NOT include the array index to survive sorting/filtering
                  const safeName = String(item?.name || 'unk').replace(/[^a-zA-Z0-9]/g, '');
                  const safeHost = String(item?.hostname || 'mult').replace(/[^a-zA-Z0-9]/g, '');
                  const safeVersion = String(item?.version || 'v').replace(/[^a-zA-Z0-9]/g, '');
                  const rowKey = `si-row-${safeHost}-${safeName}-${safeVersion}`;

                  return (
                    <div key={rowKey} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleMerged }}>
                      {restConfig.showNo ? <div className="port-ip" style={rowStyleMerged}><span>{actualNumber}</span></div> : null}
                      
                      {(!isGrouped && restConfig.showIP) ? <div className="port-ip" style={rowStyleMerged}><span>{item?.ip || '-'}</span></div> : null}
                      {(!isGrouped && restConfig.showHostname) ? <div className="port-ip" style={rowStyleMerged}><span>{item?.hostname || '-'}</span></div> : null}
                      
                      {(isGrouped && restConfig.showHostnames) ? (
                        <div className="port-ip" style={rowStyleMerged}>
                          {(item?.hostnames || '').split(', ').length > 5 ? (
                             <span title={item?.hostnames || ''}>{`${(item?.hostnames || '').split(', ').slice(0, 5).join(', ')} ... (+${(item?.hostnames || '').split(', ').length - 5} more)`}</span>
                          ) : (
                             <span>{item?.hostnames || '-'}</span>
                          )}
                        </div>
                      ) : null}
                      
                      {restConfig.showName ? <div className="port-service" style={rowStyleMerged}><span>{item?.name || '-'}</span></div> : null}
                      {restConfig.showPublisher ? <div className="port-service" style={rowStyleMerged}><span>{item?.publisher || '-'}</span></div> : null}
                      {restConfig.showVersion ? <div className="port-service" style={rowStyleMerged}><span>{item?.version || '-'}</span></div> : null}
                    </div>
                  );
                })}
              </React.Fragment>
            )}
          </div>
        </div>
    </div>
  );
};
